import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function AddClass() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Add Class form state
  const [classForm, setClassForm] = useState({
    semester: '',
    gradeId: '',
    streamId: '',
    academicYear: new Date().getFullYear().toString(),
    term: '',
    classTeacherId: '',
    assistantTeacherId: '',
    sections: [], // Sections removed from form
    subjects: [''], // Start with one empty subject
    teachers: [''], // Start with one empty teacher
    subjectSections: [''] // Start with one empty section for each subject
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);
  
  // Curriculum data
  const [grades, setGrades] = useState([]);
  const [streams, setStreams] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  
  // Filtered teachers based on selected subjects
  const [filteredTeachers, setFilteredTeachers] = useState([]);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      // Reset form when branch changes
      if (selectedBranchId !== undefined) {
        setClassForm({
          semester: '',
          gradeId: '',
          streamId: '',
          academicYear: new Date().getFullYear().toString(),
          term: '',
          classTeacherId: '',
          assistantTeacherId: '',
          sections: [],
          subjects: [''],
          teachers: [''],
          subjectSections: ['']
        });
        setFormErrors({});
      }
      fetchGrades();
      fetchTeachers();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (classForm.gradeId) {
      fetchStreamsForGrade();
      fetchSubjectsForGrade();
    } else {
      setStreams([]);
      setAvailableSubjects([]);
    }
  }, [classForm.gradeId, selectedBranchId]);

  // Function to get filtered teachers for a specific subject
  const getFilteredTeachersForSubject = (subjectName, index) => {
    if (!subjectName || !subjectName.trim() || !teachers.length) {
      return [];
    }
    
    return teachers.filter(teacher => {
      if (!teacher.subjects || !Array.isArray(teacher.subjects)) return false;
      return teacher.subjects.some(tSubject => 
        tSubject && tSubject.trim().toLowerCase() === subjectName.trim().toLowerCase()
      );
    });
  };

  // Filter teachers for class teacher and assistant teacher (only teachers assigned in Subjects & Teachers section)
  useEffect(() => {
    // Filter teachers based on teachers assigned in the Subjects & Teachers section
    if (classForm.teachers.length > 0 && teachers.length > 0) {
      // Get all teacher IDs that have been assigned in the Subjects & Teachers section
      const assignedTeacherIds = classForm.teachers
        .filter(teacherId => teacherId && teacherId.toString().trim() !== '')
        .map(teacherId => {
          // Handle both string and number IDs
          const id = typeof teacherId === 'string' ? parseInt(teacherId, 10) : teacherId;
          return isNaN(id) ? null : id;
        })
        .filter(id => id !== null);
      
      if (assignedTeacherIds.length > 0) {
        // Filter teachers to only include those assigned in Subjects & Teachers section
        const filtered = teachers.filter(teacher => 
          assignedTeacherIds.includes(teacher.id)
        );
        setFilteredTeachers(filtered);
      } else {
        setFilteredTeachers([]);
      }
    } else {
      setFilteredTeachers([]);
    }
  }, [classForm.teachers, teachers]);

  const fetchGrades = async () => {
    if (!schoolData?.id) return;

    setLoadingCurriculum(true);
    try {
      let gradesQuery = supabase
        .from('grades')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        gradesQuery = gradesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: gradesData, error: gradesError } = await gradesQuery.order('name');

      if (gradesError) throw gradesError;
      setGrades(gradesData || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoadingCurriculum(false);
    }
  };

  const fetchStreamsForGrade = async () => {
    if (!schoolData?.id || !classForm.gradeId) return;

    try {
      let streamsQuery = supabase
        .from('streams')
        .select('*')
        .eq('grade_id', classForm.gradeId);

      if (selectedBranchId) {
        streamsQuery = streamsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: streamsData, error: streamsError } = await streamsQuery.order('name');

      if (streamsError) throw streamsError;
      setStreams(streamsData || []);
    } catch (error) {
      console.error('Error fetching streams:', error);
      setStreams([]);
    }
  };

  const fetchSubjectsForGrade = async () => {
    if (!schoolData?.id || !classForm.gradeId) return;

    try {
      let subjectsQuery = supabase
        .from('subjects')
        .select('subject_name')
        .eq('grade_id', classForm.gradeId);

      if (selectedBranchId) {
        subjectsQuery = subjectsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: subjectsData, error: subjectsError } = await subjectsQuery;

      if (subjectsError) throw subjectsError;
      
      // Get distinct subject names
      const subjectNames = (subjectsData || []).map(s => s.subject_name);
      const distinctSubjects = [...new Set(subjectNames.map(name => name.toLowerCase()))]
        .map(lowerName => {
          return subjectNames.find(name => name.toLowerCase() === lowerName);
        })
        .filter(Boolean)
        .sort();

      setAvailableSubjects(distinctSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setAvailableSubjects([]);
    }
  };

  const fetchTeachers = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingTeachers(true);
      let query = supabase
        .from('teachers')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);
      
      // Filter by branch if one is selected
      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }
      
      const { data, error } = await query.order('first_name');
      
      if (error) {
        console.error('Error fetching teachers:', error);
        return;
      }
      
      setTeachers(data || []);
      
      // Reset selected teachers if they're no longer in the filtered list
      if (classForm.classTeacherId) {
        const teacherExists = data?.some(t => t.id.toString() === classForm.classTeacherId.toString());
        if (!teacherExists) {
          setClassForm(prev => ({
            ...prev,
            classTeacherId: ''
          }));
        }
      }
      if (classForm.assistantTeacherId) {
        const teacherExists = data?.some(t => t.id.toString() === classForm.assistantTeacherId.toString());
        if (!teacherExists) {
          setClassForm(prev => ({
            ...prev,
            assistantTeacherId: ''
          }));
        }
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Listen for branch change events - reset teacher selections when branch changes
  useEffect(() => {
    const handleBranchChange = () => {
      // Reset teacher selections when branch changes
      setClassForm(prev => ({
        ...prev,
        classTeacherId: '',
        assistantTeacherId: ''
      }));
    };

    window.addEventListener('branchChanged', handleBranchChange);
    return () => {
      window.removeEventListener('branchChanged', handleBranchChange);
    };
  }, []);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Generate years for dropdown (current year ± 5 years)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 5; i++) {
      years.push(i.toString());
    }
    return years;
  };

  // Add Class form functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClassForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSectionChange = (index, value) => {
    setClassForm(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? value : section
      )
    }));
    
    // Clear section errors when user starts typing
    if (formErrors[`section_${index}`]) {
      setFormErrors(prev => ({
        ...prev,
        [`section_${index}`]: ''
      }));
    }
  };

  const addSection = () => {
    setClassForm(prev => ({
      ...prev,
      sections: [...prev.sections, '']
    }));
  };

  const removeSection = (index) => {
    if (classForm.sections.length > 1) {
      setClassForm(prev => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== index)
      }));
      
      // Clear error for removed section
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`section_${index}`];
        return newErrors;
      });
    }
  };

  const handleSubjectChange = (index, value) => {
    setClassForm(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => 
        i === index ? value : subject
      )
    }));
    
    // Clear subject errors when user starts typing
    if (formErrors[`subject_${index}`]) {
      setFormErrors(prev => ({
        ...prev,
        [`subject_${index}`]: ''
      }));
    }
  };

  const addSubject = () => {
    setClassForm(prev => ({
      ...prev,
      subjects: [...prev.subjects, ''],
      teachers: [...prev.teachers, ''],
      subjectSections: [...prev.subjectSections, '']
    }));
  };

  const removeSubject = (index) => {
    if (classForm.subjects.length > 1) {
      setClassForm(prev => ({
        ...prev,
        subjects: prev.subjects.filter((_, i) => i !== index),
        teachers: prev.teachers.filter((_, i) => i !== index),
        subjectSections: prev.subjectSections.filter((_, i) => i !== index)
      }));
      
      // Clear error for removed subject
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`subject_${index}`];
        delete newErrors[`teacher_${index}`];
        delete newErrors[`section_${index}`];
        return newErrors;
      });
    }
  };

  const handleTeacherChange = (index, value) => {
    setClassForm(prev => ({
      ...prev,
      teachers: prev.teachers.map((teacher, i) => 
        i === index ? value : teacher
      )
    }));
    
    // Clear teacher errors when user starts typing
    if (formErrors[`teacher_${index}`]) {
      setFormErrors(prev => ({
        ...prev,
        [`teacher_${index}`]: ''
      }));
    }
  };

  const handleSubjectSectionChange = (index, value) => {
    setClassForm(prev => ({
      ...prev,
      subjectSections: prev.subjectSections.map((section, i) => 
        i === index ? value : section
      )
    }));
    
    // Clear error when user starts typing
    if (formErrors[`section_${index}`]) {
      setFormErrors(prev => ({
        ...prev,
        [`section_${index}`]: ''
      }));
    }
  };

  const addTeacher = () => {
    setClassForm(prev => ({
      ...prev,
      teachers: [...prev.teachers, '']
    }));
  };

  const removeTeacher = (index) => {
    if (classForm.teachers.length > 1) {
      setClassForm(prev => ({
        ...prev,
        teachers: prev.teachers.filter((_, i) => i !== index)
      }));
      
      // Clear error for removed teacher
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`teacher_${index}`];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Class name is now auto-generated, no validation needed
    if (!classForm.gradeId) {
      errors.gradeId = 'Class is required';
    }
    if (!classForm.academicYear) {
      errors.academicYear = 'Academic year is required';
    }
    if (!classForm.term) {
      errors.term = 'Term is required';
    }
    
    
    // Validate subjects (optional - no minimum requirement)
    // Subjects are now optional - removed required validation
    // Duplicate subjects are allowed for different sections
    
    // Validate teachers (optional - no minimum requirement)
    // Teachers are now optional - removed required validation
    // Duplicate teachers are allowed for different subjects/sections
    
    // Validate subject sections (optional)
    // Subject sections are now optional - removed required validation
    classForm.subjectSections.forEach((section, index) => {
      // Sections are optional - no validation required
      // if (classForm.subjects[index] && classForm.subjects[index].trim() !== '') {
      //   if (!section.trim()) {
      //     errors[`section_${index}`] = 'Section is required for this subject';
      //   }
      // }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Function to update teachers' class_ids and class_sections when a class is created
  const updateTeachersClassAssignments = async (classId, sections) => {
    try {
      const teachersToUpdate = new Set();
      
      // Add class teacher if assigned
      if (classForm.classTeacherId) {
        teachersToUpdate.add(parseInt(classForm.classTeacherId));
      }
      
      // Add assistant teacher if assigned
      if (classForm.assistantTeacherId) {
        teachersToUpdate.add(parseInt(classForm.assistantTeacherId));
      }
      
      // Add subject teachers from the teachers array
      // Note: The teachers array contains teacher names, we need to find their IDs
      const validTeachers = classForm.teachers.filter(teacher => teacher.trim() !== '');
      if (validTeachers.length > 0 && schoolData?.id) {
        // Fetch all teachers for the school to match names, filtered by branch if selected
        let allTeachersQuery = supabase
          .from('teachers')
          .select('id, first_name, last_name')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);
        
        // Filter by branch if one is selected
        if (selectedBranchId) {
          allTeachersQuery = allTeachersQuery.eq('branch_id', selectedBranchId);
        }
        
        const { data: allTeachers } = await allTeachersQuery;
        
        if (allTeachers) {
          validTeachers.forEach(teacherName => {
            // Try to match teacher name (could be full name or just first/last)
            const matchedTeacher = allTeachers.find(t => {
              const fullName = `${t.first_name} ${t.last_name}`.toLowerCase();
              const firstName = t.first_name.toLowerCase();
              const lastName = t.last_name.toLowerCase();
              const searchName = teacherName.trim().toLowerCase();
              
              return fullName === searchName || 
                     firstName === searchName || 
                     lastName === searchName ||
                     fullName.includes(searchName) ||
                     searchName.includes(firstName) ||
                     searchName.includes(lastName);
            });
            
            if (matchedTeacher) {
              teachersToUpdate.add(matchedTeacher.id);
            }
          });
        }
      }
      
      // Update each teacher's class_ids and class_sections
      for (const teacherId of teachersToUpdate) {
        // Fetch current teacher data
        const { data: teacherData, error: fetchError } = await supabase
          .from('teachers')
          .select('class_ids, class_sections')
          .eq('id', teacherId)
          .single();
        
        if (fetchError) {
          console.error(`Error fetching teacher ${teacherId}:`, fetchError);
          continue;
        }
        
        // Get current class_ids and class_sections
        const currentClassIds = teacherData.class_ids || [];
        const currentClassSections = teacherData.class_sections || {};
        
        // Add new class_id if not already present
        const updatedClassIds = currentClassIds.includes(classId) 
          ? currentClassIds 
          : [...currentClassIds, classId];
        
        // Update class_sections to include all sections for this class
        const updatedClassSections = {
          ...currentClassSections,
          [classId]: sections // Assign all sections to this teacher for this class
        };
        
        // Update teacher record
        const { error: updateError } = await supabase
          .from('teachers')
          .update({
            class_ids: updatedClassIds,
            class_sections: updatedClassSections
          })
          .eq('id', teacherId);
        
        if (updateError) {
          console.error(`Error updating teacher ${teacherId}:`, updateError);
        } else {
          console.log(`Successfully updated teacher ${teacherId} with class ${classId}`);
        }
      }
    } catch (err) {
      console.error('Error updating teachers class assignments:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedBranchId) {
      setFormErrors(prev => ({
        ...prev,
        branch: 'Please select a branch first before adding a class.'
      }));
      alert('Please select a branch first before adding a class.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Filter out empty subjects and teachers and create class data
      const validSubjects = classForm.subjects.filter(subject => subject.trim() !== '');
      const validTeachers = classForm.teachers.filter(teacher => teacher.trim() !== '');

      // Auto-generate class name from grade and stream
      let generatedClassName = 'Class'; // Default fallback
      if (classForm.gradeId) {
        try {
          const { data: gradeData } = await supabase
            .from('grades')
            .select('name')
            .eq('id', classForm.gradeId)
            .single();
          
          if (classForm.streamId) {
            const { data: streamData } = await supabase
              .from('streams')
              .select('name')
              .eq('id', classForm.streamId)
              .single();
            
            if (gradeData?.name && streamData?.name) {
              generatedClassName = `${gradeData.name} - ${streamData.name}`;
            } else if (gradeData?.name) {
              generatedClassName = gradeData.name;
            } else {
              generatedClassName = `Grade ${classForm.gradeId} - Stream ${classForm.streamId}`;
            }
          } else if (gradeData?.name) {
            generatedClassName = gradeData.name;
          } else {
            generatedClassName = `Grade ${classForm.gradeId}`;
          }
        } catch (nameErr) {
          console.warn('Error fetching grade/stream names for class name generation:', nameErr);
          // If we can't fetch names, use a default format
          generatedClassName = classForm.streamId 
            ? `Grade ${classForm.gradeId} - Stream ${classForm.streamId}`
            : `Grade ${classForm.gradeId}`;
        }
      }

      // Get valid sections (filter out empty ones)
      console.log('=== SAVING CLASS ===');
      console.log('Class form sections (raw):', classForm.sections);
      console.log('Class form streamId:', classForm.streamId);
      
      // If sections array is empty but streamId is set, get section name from stream
      let sectionsToSave = [...classForm.sections];
      if (sectionsToSave.length === 0 && classForm.streamId) {
        // First try to find in local streams array
        let selectedStream = streams.find(s => s.id.toString() === classForm.streamId.toString());
        
        // If not found locally, fetch from database
        if (!selectedStream) {
          try {
            const { data: streamData, error: streamError } = await supabase
              .from('streams')
              .select('name')
              .eq('id', classForm.streamId)
              .single();
            
            if (!streamError && streamData) {
              selectedStream = { id: classForm.streamId, name: streamData.name };
            }
          } catch (fetchErr) {
            console.error('Error fetching stream:', fetchErr);
          }
        }
        
        if (selectedStream && selectedStream.name) {
          sectionsToSave = [selectedStream.name];
          console.log('No sections in array, but streamId is set. Adding section from stream:', selectedStream.name);
        }
      }
      
      const validSections = sectionsToSave.filter(section => section && section.trim() !== '');
      console.log('Valid sections to save:', validSections);

      // De-duplicate sections (case-insensitive) and block if duplicates were provided
      const normalizedSections = validSections.map(s => s.trim()).filter(Boolean);
      const sectionSeen = new Set();
      const duplicateSections = [];
      normalizedSections.forEach((s) => {
        const key = s.toLowerCase();
        if (sectionSeen.has(key)) duplicateSections.push(s);
        sectionSeen.add(key);
      });
      if (duplicateSections.length > 0) {
        setFormErrors(prev => ({
          ...prev,
          gradeId: `Duplicate section(s) are not allowed: ${[...new Set(duplicateSections)].join(', ')}`
        }));
        setIsSubmitting(false);
        return;
      }

      // Prevent duplicate class + section for the same Academic Year + Term (+ Branch)
      // Class name is generated as "Grade - Section", so matching class_name covers the class+section uniqueness.
      try {
        const academicYearInt = classForm.academicYear ? parseInt(classForm.academicYear, 10) : null;
        const termInt = classForm.term ? parseInt(classForm.term, 10) : null;

        let existingQuery = supabase
          .from('classes')
          .select('id, class_name')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .ilike('class_name', generatedClassName) // exact match, case-insensitive (no wildcards)
          .eq('academic_year', academicYearInt)
          .eq('term', termInt);

        if (selectedBranchId) {
          existingQuery = existingQuery.eq('branch_id', selectedBranchId);
        } else {
          existingQuery = existingQuery.is('branch_id', null);
        }

        const { data: existingClasses, error: existingError } = await existingQuery.limit(1);
        if (existingError) {
          console.warn('Could not check for duplicate class:', existingError);
        } else if (existingClasses && existingClasses.length > 0) {
          setFormErrors(prev => ({
            ...prev,
            gradeId: `This class already exists: "${generatedClassName}" (Academic Year ${classForm.academicYear}, Term ${classForm.term}).`
          }));
          setIsSubmitting(false);
          return;
        }
      } catch (dupErr) {
        console.warn('Duplicate class check failed:', dupErr);
      }
      
      const classData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null, // Include branch_id if a branch is selected
        class_name: generatedClassName,
        grade_id: classForm.gradeId ? parseInt(classForm.gradeId) : null,
        stream_id: classForm.streamId ? parseInt(classForm.streamId) : null,
        academic_year: classForm.academicYear ? parseInt(classForm.academicYear) : null,
        term: classForm.term ? parseInt(classForm.term) : null,
        class_teacher_id: classForm.classTeacherId ? parseInt(classForm.classTeacherId) : null,
        assistant_teacher_id: classForm.assistantTeacherId ? parseInt(classForm.assistantTeacherId) : null,
        sections: validSections, // Include sections from the selected section/stream
        subjects: validSubjects, // Store as array (validation ensures it's not empty)
        teachers: validTeachers, // Store as array (validation ensures it's not empty)
        subject_sections: {}, // Empty object since sections are removed
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      console.log('Saving class data to Supabase:', classData);
      
      const { data, error } = await supabase
        .from('classes')
        .insert(classData)
        .select()
        .single();
      
      if (error) {
        console.error('Error saving class:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        // Provide more specific error message
        let errorMessage = 'Failed to add class. Please try again.';
        if (error.message) {
          errorMessage += `\n\nError: ${error.message}`;
        }
        if (error.hint) {
          errorMessage += `\n\nHint: ${error.hint}`;
        }
        alert(errorMessage);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Class added successfully:', data);
      
      // Verify the saved class data
      if (data) {
        console.log('=== VERIFICATION AFTER INSERT ===');
        console.log('Saved class data from database:', data);
        console.log('- Sections in DB:', data.sections);
        console.log('- Sections type:', typeof data.sections);
        console.log('- Is sections array?', Array.isArray(data.sections));
        console.log('- Sections length:', data.sections?.length);
        console.log('=== END VERIFICATION ===');
      }
      
      // Update teachers' class_ids and class_sections (pass valid sections)
      await updateTeachersClassAssignments(data.id, validSections);
      
      // Store success data
      setSuccessData({
        className: generatedClassName
      });
      
      // Reset form
      setClassForm({
        semester: '',
        gradeId: '',
        streamId: '',
        academicYear: new Date().getFullYear().toString(),
        term: '',
        classTeacherId: '',
        assistantTeacherId: '',
        sections: [], // Empty array since sections are removed
        subjects: [''],
        teachers: [''],
        subjectSections: ['']
      });
      setFormErrors({});
      
      // Show success popup
      setShowSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add class. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Add New Class</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="dashboard-content">
            {/* Branch Selector Section */}
            <div className="dashboard-branch-row schedule-view-branch-row" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <BranchSelector
                schoolId={schoolData?.id}
                selectedBranchId={selectedBranchId}
                onBranchChange={handleBranchChange}
              />
              <div className="branch-context" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>
                {selectedBranchId 
                  ? `📍 Adding class for the selected branch` 
                  : '⚠️ Please select a branch to add a class'}
              </div>
            </div>

            {/* Show message if no branch is selected */}
            {!selectedBranchId ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem 2rem', 
                background: '#F9FAFB', 
                borderRadius: '8px', 
                border: '1px solid #E5E7EB',
                marginBottom: '2rem'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📍</div>
                <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Select a Branch</h3>
                <p style={{ color: '#6B7280', fontSize: '1.1rem', lineHeight: '1.6' }}>
                  Please select a branch from the dropdown above to add a class for that branch.
                </p>
              </div>
            ) : (
              <div className="add-class-section">
                <div className="add-class-card">
                  <form onSubmit={handleSubmit} className="add-class-form">
                      <div className="form-section">
                        <h3>Class Information</h3>
                      
                      {/* Grade and Section */}
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="gradeId">Class *</label>
                          <select
                            id="gradeId"
                            name="gradeId"
                            value={classForm.gradeId}
                            onChange={(e) => {
                              setClassForm(prev => ({
                                ...prev,
                                gradeId: e.target.value,
                                streamId: '', // Reset stream when grade changes
                                sections: [], // Reset sections when grade changes
                                subjects: [''], // Reset subjects when grade changes
                                teachers: ['']
                              }));
                            }}
                            className={formErrors.gradeId ? 'error' : ''}
                          >
                            <option value="">-- Select Class --</option>
                            {loadingCurriculum ? (
                              <option disabled>Loading classes...</option>
                            ) : grades.map(grade => (
                              <option key={grade.id} value={grade.id}>
                                {grade.name}
                              </option>
                            ))}
                          </select>
                          {formErrors.gradeId && (
                            <span className="error-text">{formErrors.gradeId}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="streamId">Section</label>
                          <select
                            id="streamId"
                            name="streamId"
                            value={classForm.streamId}
                            onChange={(e) => {
                              const selectedStreamId = e.target.value;
                              const selectedStream = streams.find(s => s.id.toString() === selectedStreamId);
                              
                              setClassForm(prev => {
                                // If a section is selected, add it to the sections array
                                // If no section is selected, clear the sections array
                                let updatedSections = [];
                                if (selectedStream && selectedStream.name) {
                                  // Add the selected section name to the sections array
                                  updatedSections = [selectedStream.name];
                                }
                                
                                return {
                                  ...prev,
                                  streamId: selectedStreamId,
                                  sections: updatedSections
                                };
                              });
                            }}
                            disabled={!classForm.gradeId}
                          >
                            <option value="">-- Select Section (Optional) --</option>
                            {streams.map(stream => (
                              <option key={stream.id} value={stream.id}>
                                {stream.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Academic Year and Term */}
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="academicYear">Academic Year *</label>
                          <select
                            id="academicYear"
                            name="academicYear"
                            value={classForm.academicYear}
                            onChange={handleInputChange}
                            className={formErrors.academicYear ? 'error' : ''}
                          >
                            {generateYears().map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                          {formErrors.academicYear && (
                            <span className="error-text">{formErrors.academicYear}</span>
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="term">Term *</label>
                          <select
                            id="term"
                            name="term"
                            value={classForm.term}
                            onChange={handleInputChange}
                            className={formErrors.term ? 'error' : ''}
                          >
                            <option value="">-- Select Term --</option>
                            <option value="1">Term 1</option>
                            <option value="2">Term 2</option>
                            <option value="3">Term 3</option>
                          </select>
                          {formErrors.term && (
                            <span className="error-text">{formErrors.term}</span>
                          )}
                        </div>
                      </div>
                      
                    </div>
                    
                    <div className="form-section">
                      <div className="subjects-header">
                        <h4>Subjects & Teachers</h4>
                        <button 
                          type="button" 
                          className="add-subject-btn"
                          onClick={addSubject}
                        >
                          + Add Subject
                        </button>
                      </div>
                      
                      {formErrors.subjects && (
                        <span className="error-text">{formErrors.subjects}</span>
                      )}
                      
                      {formErrors.teachers && (
                        <span className="error-text">{formErrors.teachers}</span>
                      )}
                      
                      <div className="subjects-list">
                        {classForm.subjects.map((subject, index) => (
                          <div key={index} className="subject-teacher-item">
                            <div className="subject-input-group">
                              <select
                                value={subject}
                                onChange={(e) => handleSubjectChange(index, e.target.value)}
                                className={formErrors[`subject_${index}`] ? 'error' : ''}
                                disabled={!classForm.gradeId}
                              >
                                <option value="">
                                  {!classForm.gradeId 
                                    ? 'Select class first' 
                                    : availableSubjects.length === 0
                                    ? 'No subjects found for this class'
                                    : 'Select Subject'}
                                </option>
                                {availableSubjects.length > 0 && availableSubjects.map((subjectName, idx) => (
                                  <option key={idx} value={subjectName}>
                                    {subjectName}
                                  </option>
                                ))}
                              </select>
                              {formErrors[`subject_${index}`] && (
                                <span className="error-text">{formErrors[`subject_${index}`]}</span>
                              )}
                            </div>
                            
                            <div className="teacher-input-group">
                              <select
                                value={classForm.teachers[index] || ''}
                                onChange={(e) => handleTeacherChange(index, e.target.value)}
                                className={formErrors[`teacher_${index}`] ? 'error' : ''}
                                disabled={!classForm.subjects[index] || classForm.subjects[index].trim() === ''}
                              >
                                <option value="">
                                  {!classForm.subjects[index] || classForm.subjects[index].trim() === '' 
                                    ? 'Select subject first' 
                                    : loadingTeachers 
                                    ? 'Loading teachers...' 
                                    : getFilteredTeachersForSubject(classForm.subjects[index], index).length === 0
                                    ? 'No teachers found for this subject'
                                    : 'Select Teacher'}
                                </option>
                                {getFilteredTeachersForSubject(classForm.subjects[index], index).map((teacher) => (
                                  <option key={teacher.id} value={teacher.id}>
                                    {teacher.first_name} {teacher.last_name}
                                  </option>
                                ))}
                              </select>
                              {formErrors[`teacher_${index}`] && (
                                <span className="error-text">{formErrors[`teacher_${index}`]}</span>
                              )}
                            </div>
                            
                            {classForm.subjects.length > 1 && (
                              <button
                                type="button"
                                className="remove-subject-btn"
                                onClick={() => removeSubject(index)}
                                title="Remove subject and teacher"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Class Teacher and Assistant Teacher - After Subjects/Teachers */}
                    <div className="form-section">
                      <h3>Class Teachers</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="classTeacherId">Class Teacher</label>
                          <select
                            id="classTeacherId"
                            name="classTeacherId"
                            value={classForm.classTeacherId}
                            onChange={handleInputChange}
                            disabled={filteredTeachers.length === 0}
                          >
                            <option value="">
                              {filteredTeachers.length === 0 
                                ? 'Assign teachers in Subjects & Teachers section first' 
                                : 'Select Class Teacher'}
                            </option>
                            {filteredTeachers.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.first_name} {t.last_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor="assistantTeacherId">Assistant Teacher</label>
                          <select
                            id="assistantTeacherId"
                            name="assistantTeacherId"
                            value={classForm.assistantTeacherId}
                            onChange={handleInputChange}
                            disabled={filteredTeachers.length === 0}
                          >
                            <option value="">
                              {filteredTeachers.length === 0 
                                ? 'Assign teachers in Subjects & Teachers section first' 
                                : 'Select Assistant Teacher'}
                            </option>
                            {filteredTeachers.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.first_name} {t.last_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => navigate('/school/dashboard')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Adding Class...' : 'Add Class'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Beautiful Success Popup */}
      {showSuccessPopup && successData && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => {
            setShowSuccessPopup(false);
            setSuccessData(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 139, 61, 0.1) 0%, rgba(230, 122, 53, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 139, 61, 0.1) 0%, rgba(230, 122, 53, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Class Added Successfully! 🎉
            </h2>

            {/* Subtitle */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              position: 'relative',
              zIndex: 1
            }}>
              Your class has been created and is ready to use.
            </p>

            {/* Class Details */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                textAlign: 'left'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#6B7280', fontWeight: '500' }}>Class Name:</span>
                  <span style={{ color: '#1F2937', fontWeight: '600' }}>{successData.className}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  setSuccessData(null);
                  navigate('/school/classes');
                }}
                style={{
                  background: 'linear-gradient(135deg, #FF8B3D 0%, #E67A35 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 139, 61, 0.3)',
                  transition: 'all 0.3s ease',
                  flex: 1,
                  maxWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 139, 61, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 139, 61, 0.3)';
                }}
              >
                View Classes
              </button>
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  setSuccessData(null);
                }}
                style={{
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flex: 1,
                  maxWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#E5E7EB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#F3F4F6';
                }}
              >
                Add Another
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default AddClass;
